// src/pages/Feedback.js

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ZaraAssistant from "../components/ZaraAssistant";
import { useLanguage } from "../context/LanguageContext";
import translations from "../data/translations";
import { apiUrl } from "../config/apiConfig";

const FEEDBACK_CATEGORIES = [
  { id: "service_quality", labelKey: "fCat1" },
  { id: "support_team",    labelKey: "fCat2" },
  { id: "app_experience",  labelKey: "fCat3" },
  { id: "pricing",         labelKey: "fCat4" },
  { id: "claims_process",  labelKey: "fCat5" },
  { id: "other",           labelKey: "fCat6" },
];

const EMOJI_KEYS = ["😠", "😕", "😐", "😊", "🤩"];

const StepBar = ({ current, total, color }) => (
  <div className="d-flex align-items-center gap-2 mb-4">
    {Array.from({ length: total }, (_, i) => {
      const done = i + 1 < current;
      const active = i + 1 === current;
      return (
        <React.Fragment key={i}>
          <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: 28, height: 28, background: done || active ? color : "#e6eef3", color: done || active ? "#fff" : "#aaa", fontSize: "0.72rem", fontWeight: 700, transition: "background 0.3s" }}>
            {done ? "✓" : i + 1}
          </div>
          {i < total - 1 && (
            <div style={{ flex: 1, height: 3, borderRadius: 2, background: done ? color : "#e6eef3", transition: "background 0.3s" }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const Feedback = () => {
  const navigate = useNavigate();
  const { service, phone, network } = useLocation().state || {};
  const [step, setStep] = useState(1);
  const [rating, setRating] = useState(null);
  const [category, setCategory] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [submissionMessage, setSubmissionMessage] = useState("");

  const { language } = useLanguage();
  const t = translations[language] || translations["English"];

  const serviceName = service?.name || `${network || ""} ${t.serviceLabel || "Service"}`.trim();

  const handleSubmitFeedback = async () => {
    setApiError("");
    setSubmitting(true);

    try {
      const body = {
        phone,
        network,
        service_name: serviceName,
        rating,
        category,
        comment,
        language,
      };

      const res = await fetch(apiUrl("/feedback"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || t.feedbackSubmitFailed || "Unable to submit feedback. Please try again.");
      }

      setSubmissionMessage(json.message || t.feedbackSuccessMessage || "Thank you for your feedback.");
      setStep(3);
    } catch (err) {
      setApiError(err.message || t.feedbackSubmitFailed || "Unable to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const EMOJIS = EMOJI_KEYS.map((emoji, i) => ({ val: i + 1, emoji, label: t.ratingLabel[i] }));
  const CATEGORIES = FEEDBACK_CATEGORIES.map(c => ({ ...c, label: t[c.labelKey] || c.id }));
  const chosen = EMOJIS.find(e => e.val === rating);

  const stepTitles = [t.feedbackStep1Title, t.feedbackStep2Title, t.feedbackDoneTitle];

  return (
    <div className="container-fluid py-4 px-3 px-md-4" style={{ maxWidth: "900px", margin: "0 auto" }}>
      <Header showBack onBack={() => step > 1 && step < 3 ? setStep(s => s - 1) : navigate(-1)} />

      <div className="card shadow-sm border-0 rounded-4 p-4 mb-4">

        {/* Header */}
        <div className="d-flex align-items-center gap-3 mb-4">
          <div className="d-flex align-items-center justify-content-center rounded-circle text-white fw-bold flex-shrink-0"
            style={{ width: 42, height: 42, background: "#2ecc71", fontSize: "1rem" }}>
            💚
          </div>
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e", marginBottom: "2px" }}>{t.feedbackPageTitle}</p>
            <p style={{ fontSize: "0.78rem", color: "#888", marginBottom: 0 }}>{t.feedbackPageSub}</p>
          </div>
        </div>

        <StepBar current={step} total={3} color="#2ecc71" />

        <p style={{ fontSize: "0.72rem", color: "#2ecc71", fontWeight: 700, letterSpacing: "0.5px", marginBottom: "16px", textTransform: "uppercase" }}>
          Step {step} / 3 — {stepTitles[step - 1]}
        </p>

        {/* ── Step 1: Rating + Category ── */}
        {step === 1 && (
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e", marginBottom: "4px" }}>{t.feedbackStep1Title}</p>
            <p style={{ fontSize: "0.8rem", color: "#888", marginBottom: "24px" }}>{t.feedbackStep1Sub}</p>

            <p style={{ fontWeight: 600, fontSize: "0.85rem", color: "#1a1a2e", marginBottom: "12px" }}>{t.feedbackRatingLabel}</p>
            <div className="d-flex justify-content-between gap-2 mb-4">
              {EMOJIS.map((e) => (
                <button key={e.val} onClick={() => setRating(e.val)}
                  style={{
                    flex: 1, border: "none",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",
                    cursor: "pointer", padding: "8px 4px", borderRadius: "10px",
                    background: rating === e.val ? "#e6f5fb" : "transparent",
                    outline: rating === e.val ? "2px solid #0099CC" : "2px solid transparent",
                    transition: "all 0.15s"
                  }}>
                  <span style={{ fontSize: "1.8rem", lineHeight: 1 }}>{e.emoji}</span>
                  <span style={{ fontSize: "0.62rem", color: rating === e.val ? "#0099CC" : "#aaa", fontWeight: rating === e.val ? 700 : 400 }}>
                    {e.label}
                  </span>
                </button>
              ))}
            </div>

            <p style={{ fontWeight: 600, fontSize: "0.85rem", color: "#1a1a2e", marginBottom: "12px" }}>{t.feedbackCategoryLabel}</p>
            <div className="row g-2 mb-4">
              {CATEGORIES.map((c) => (
                <div key={c.id} className="col-6">
                  <button onClick={() => setCategory(c.id)}
                    style={{
                      width: "100%", padding: "10px 8px", borderRadius: "10px", cursor: "pointer",
                      fontSize: "0.78rem", fontWeight: category === c.id ? 700 : 400,
                      color: category === c.id ? "#0099CC" : "#555",
                      background: category === c.id ? "#e6f5fb" : "#fafafa",
                      border: `1.5px solid ${category === c.id ? "#0099CC" : "#e6eef3"}`,
                      transition: "all 0.15s"
                    }}>
                    {c.label}
                  </button>
                </div>
              ))}
            </div>

            <button onClick={() => setStep(2)} disabled={!rating || !category}
              style={{ width: "100%", padding: "13px", background: !rating || !category ? "#ccc" : "#0099CC", color: "white", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: !rating || !category ? "not-allowed" : "pointer", transition: "background 0.2s" }}
              onMouseEnter={e => { if (rating && category) e.currentTarget.style.background = "#007aaa"; }}
              onMouseLeave={e => { if (rating && category) e.currentTarget.style.background = "#0099CC"; }}>
              {t.feedbackNextBtn}
            </button>
          </div>
        )}

        {/* ── Step 2: Comment ── */}
        {step === 2 && (
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e", marginBottom: "4px" }}>{t.feedbackStep2Title}</p>
            <p style={{ fontSize: "0.8rem", color: "#888", marginBottom: "20px" }}>{t.feedbackStep2Sub}</p>

            <div className="d-flex align-items-center gap-3 rounded-3 p-3 mb-4"
              style={{ background: "#e6f5fb", border: "1px solid #b8dff0" }}>
              <span style={{ fontSize: "2rem" }}>{chosen?.emoji}</span>
              <div>
                <p style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0099CC", marginBottom: "2px" }}>{chosen?.label}</p>
                <p style={{ fontSize: "0.75rem", color: "#2c5f7a", marginBottom: 0 }}>
                  {rating}/5 ★
                </p>
              </div>
            </div>

            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#1a1a2e", marginBottom: "8px" }}>
              {t.feedbackCommentLabel}
            </label>
            <textarea value={comment} onChange={e => setComment(e.target.value)}
              placeholder={t.feedbackCommentPlaceholder} rows={5}
              style={{ width: "100%", padding: "12px", border: "1.5px solid #dde", borderRadius: "10px", fontSize: "0.85rem", fontFamily: "inherit", resize: "vertical", outline: "none", marginBottom: "20px", lineHeight: 1.6 }}
              onFocus={e => e.target.style.borderColor = "#0099CC"}
              onBlur={e => e.target.style.borderColor = "#dde"} />

            {apiError && (
              <div style={{ background: "#fde2e2", color: "#9d1b1b", borderRadius: "12px", padding: "12px", marginBottom: "16px", fontSize: "0.88rem" }}>
                {apiError}
              </div>
            )}
            <button onClick={handleSubmitFeedback}
              disabled={submitting}
              style={{ width: "100%", padding: "13px", background: submitting ? "#77c4e8" : "#0099CC", color: "white", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", marginBottom: "10px", transition: "background 0.2s" }}
              onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = "#007aaa"; }}
              onMouseLeave={e => { if (!submitting) e.currentTarget.style.background = "#0099CC"; }}>
              {submitting ? (t.feedbackSubmitting || "Submitting...") : t.feedbackSubmitBtn}
            </button>
            <button onClick={() => setStep(1)}
              style={{ width: "100%", padding: "13px", background: "white", color: "#0099CC", border: "1.5px solid #0099CC", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#e6f5fb"}
              onMouseLeave={e => e.currentTarget.style.background = "white"}>
              {t.feedbackBackBtn}
            </button>
          </div>
        )}

        {/* ── Step 3: Thank you ── */}
        {step === 3 && (
          <div className="text-center py-4">
            <div style={{ width: 76, height: 76, borderRadius: "50%", background: "#edfaf3", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.2rem" }}>💚</div>
            <p style={{ fontWeight: 700, fontSize: "1.1rem", color: "#1a1a2e", marginBottom: "8px" }}>{t.feedbackDoneTitle}</p>
            <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "6px" }}>{submissionMessage || t.feedbackDoneSub}</p>
            <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "32px" }}>{t.feedbackDoneSub2}</p>
            <button onClick={() => navigate("/")}
              style={{ padding: "13px 36px", background: "#0099CC", color: "white", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#007aaa"}
              onMouseLeave={e => e.currentTarget.style.background = "#0099CC"}>
              {t.goHomeBtn}
            </button>
          </div>
        )}

        {step < 3 && (
          <ZaraAssistant message={t.ZARAMessage} features={t.ZARAFeatures || ["Quick & Easy", "Safe & Secure", "24/7 Support"]} />
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Feedback;